import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  DocumentFoldersClient,
  DocumentFolderDto,
  FolderPermissionDto,
} from '../../app/services/api/documentsClient';
import { DocumentPermission } from '../../enums/DocumentPermission';

const foldersClient = new DocumentFoldersClient();

// Mock roles for demo
const availableRoles = [
  { id: 1, name: 'Franchise Admin' },
  { id: 2, name: 'Store Manager' },
  { id: 3, name: 'Assistant Manager' },
  { id: 4, name: 'Team Member' },
  { id: 5, name: 'Trainee' },
];

const FolderPermissions: React.FC = () => {
  const { t } = useTranslation('documents');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);

  const canManage = hasPermissionTo ? hasPermissionTo(['DocumentsManagePermissions']) : true;

  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState<DocumentFolderDto | null>(null);
  const [permissions, setPermissions] = useState<FolderPermissionDto[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // New permission form
  const [newPermission, setNewPermission] = useState<{
    roleId: number | '';
    permissions: DocumentPermission[];
  }>({
    roleId: '',
    permissions: [DocumentPermission.View],
  });

  const loadData = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [f, perms] = await Promise.all([
        foldersClient.getFolder(Number(id)),
        foldersClient.getFolderPermissions(Number(id)),
      ]);
      setFolder(f);
      setPermissions(perms);
    } catch (error) {
      console.error('Error loading folder permissions:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePermissionToggle = (permission: DocumentPermission) => {
    setNewPermission(prev => {
      const current = prev.permissions;
      if (current.includes(permission)) {
        return {
          ...prev,
          permissions: current.filter(p => p !== permission),
        };
      } else {
        return {
          ...prev,
          permissions: [...current, permission],
        };
      }
    });
  };

  const handleAddPermission = async () => {
    if (!folder || !newPermission.roleId) return;

    setSaving(true);
    try {
      const created = await foldersClient.setFolderPermission(folder.id, {
        roleId: Number(newPermission.roleId),
        permissions: newPermission.permissions,
      });
      setPermissions(prev => [...prev, created]);
      setAddDialogOpen(false);
      setNewPermission({ roleId: '', permissions: [DocumentPermission.View] });
    } catch (error) {
      console.error('Error adding permission:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePermission = async (permissionId: number) => {
    if (!window.confirm(t('confirmRemovePermission', 'Are you sure you want to remove this permission?'))) {
      return;
    }

    try {
      await foldersClient.removeFolderPermission(permissionId);
      setPermissions(prev => prev.filter(p => p.id !== permissionId));
    } catch (error) {
      console.error('Error removing permission:', error);
    }
  };

  const getRoleName = (roleId: number | null | undefined) => {
    if (!roleId) return t('unknownRole', 'Unknown Role');
    return availableRoles.find(r => r.id === roleId)?.name ?? t('roleFallback', 'Role {{roleId}}', { roleId });
  };

  const allPermissions = Object.values(DocumentPermission);

  if (!canManage) {
    return (
      <PageArea>
        <PageTitle>{t('permissionsPageTitle', 'Folder Permissions')}</PageTitle>
        <Alert severity="error">
          {t('noAccessMessage', 'You do not have permission to manage folder permissions.')}
        </Alert>
      </PageArea>
    );
  }

  if (loading) {
    return (
      <PageArea>
        <LinearProgress />
      </PageArea>
    );
  }

  if (!folder) {
    return (
      <PageArea>
        <PageTitle>{t('folderNotFoundTitle', 'Folder Not Found')}</PageTitle>
        <Typography color="error">{t('folderNotFoundText', 'The requested folder could not be found.')}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/documents')}
          sx={{ mt: 2 }}
        >
          {t('backToDocuments', 'Back to Documents')}
        </Button>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate(`/documents?folderId=${folder.id}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <PageTitle subTitle={folder.path.join(' / ')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon />
              {t('managePermissionsTitle', 'Manage Permissions')}
            </Box>
          </PageTitle>
        </Box>
        <Button
          variant="primary"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          {t('addPermissionButton', 'Add Permission')}
        </Button>
      </Box>

      <Section>
        <TableHeader>
          {t('folderPermissionsTableTitle', 'Folder Permissions')}
        </TableHeader>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('columnRoleUser', 'Role / User')}</TableCell>
                <TableCell>{t('columnView', 'View')}</TableCell>
                <TableCell>{t('columnUpload', 'Upload')}</TableCell>
                <TableCell>{t('columnDownload', 'Download')}</TableCell>
                <TableCell>{t('columnEdit', 'Edit')}</TableCell>
                <TableCell>{t('columnDelete', 'Delete')}</TableCell>
                <TableCell>{t('columnManage', 'Manage')}</TableCell>
                <TableCell>{t('columnCreateFolder', 'Create Folder')}</TableCell>
                <TableCell width="60px"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}
                    align="center"
                    sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('noPermissionsConfigured', 'No permissions configured. Add a role to set permissions.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {permissions.map((perm) => (
                <TableRow key={perm.id}>
                  <TableCell>
                    <Chip
                      label={getRoleName(perm.roleId)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    {perm.storeId && (
                      <Chip
                        label={t('storeChipLabel', 'Store {{storeId}}', { storeId: perm.storeId })}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {perm.permissions.includes(DocumentPermission.View) ? '✓' : '—'}
                  </TableCell>
                  <TableCell>
                    {perm.permissions.includes(DocumentPermission.Upload) ? '✓' : '—'}
                  </TableCell>
                  <TableCell>
                    {perm.permissions.includes(DocumentPermission.Download) ? '✓' : '—'}
                  </TableCell>
                  <TableCell>
                    {perm.permissions.includes(DocumentPermission.Edit) ? '✓' : '—'}
                  </TableCell>
                  <TableCell>
                    {perm.permissions.includes(DocumentPermission.Delete) ? '✓' : '—'}
                  </TableCell>
                  <TableCell>
                    {perm.permissions.includes(DocumentPermission.ManagePermissions) ? '✓' : '—'}
                  </TableCell>
                  <TableCell>
                    {perm.permissions.includes(DocumentPermission.CreateFolder) ? '✓' : '—'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePermission(perm.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Box sx={{ mt: 2 }}>
        <Alert severity="info">
          <Typography variant="textSM">
            <strong>{t('permissionInheritanceTitle', 'Permission Inheritance:')}</strong> {t('permissionInheritanceText', 'Permissions are inherited by child folders. Users with permissions on a parent folder will have the same permissions on all subfolders unless explicitly overridden.')}
          </Typography>
        </Alert>
      </Box>

      {/* Add Permission Dialog */}
      <Dialog open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('addPermissionDialogTitle', 'Add Permission')}</DialogTitle>
        <DialogContent>
          <Grid container
            spacing={2}
            sx={{ mt: 1 }}>
            <Grid item
              xs={12}>
              <TextField
                fullWidth
                select
                label={t('roleLabel', 'Role')}
                value={newPermission.roleId}
                onChange={(e) => setNewPermission(prev => ({
                  ...prev,
                  roleId: Number(e.target.value),
                }))}
                required
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.id}
                    value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}>
              <Typography
                variant="textMD"
                gutterBottom
              >
                {t('permissionsLabel', 'Permissions')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {allPermissions.map((permission) => (
                  <FormControlLabel
                    key={permission}
                    control={
                      <Checkbox
                        checked={newPermission.permissions.includes(permission)}
                        onChange={() => handlePermissionToggle(permission)}
                        size="small"
                      />
                    }
                    label={permission}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1,
                      m: 0,
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>{t('cancel', 'Cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleAddPermission}
            disabled={!newPermission.roleId || newPermission.permissions.length === 0 || saving}
          >
            {saving ? t('adding', 'Adding...') : t('add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default FolderPermissions;
